import React from "react";
import { Link, useLocation, useNavigate, useSearchParams } from "react-router";
import { toast } from "../../../shared/ui/toast";

import { useAuth } from "../../auth/AuthContext";
import { useItemContext } from "../model/ItemContext";
import type { ApiErrorSetter } from "../../../shared/api/helpers";
import { fetchItemGroupList, updateItemGroup } from "../../item-groups/item-groups.api";
import type { ItemGroupSummary } from "../../item-groups/item-groups.api";
import { fetchShopList } from "../../shops/shops.api";
import { retrieveUserConfig, updateUserConfig, retrieveUserProfile } from "../../../api/account";
import { updateItem } from "../items.api";
import { canAccessItemImport, hasSupportedImportShop } from "../itemImportAccess";
import { ItemBatchActionModal } from "../components/ItemBatchActionModal";
import {
  createItemListColumnPreferences,
  normalizeItemListColumnPreferences,
  resolveItemListColumnConfig,
  ItemListColumnPreferences,
} from "../model/item-list.types";
import { ItemBatchActionType } from "../model/item-batch.types";
import { filterItemListItems, ItemListFilters, ItemStatusFilter, KeepDefaultPriceFilter, CompareRangeFilter, MarketSiteFilter, AccumulatedErrorFilter, normalizeItemStatusFilter, normalizeKeepDefaultPriceFilter, normalizeCompareRangeFilter, normalizeMarketSiteFilter, normalizeAccumulatedErrorFilter } from "../components/ItemListFilters";
import { ItemListBatchAction } from "../components/ItemListBatchAction";
import { canShowInhouseFeatures } from "../../../shared/lib/inhouseFeatures";
import { api } from "../../../shared/api";
import { ItemListSearchInput } from "../components/ItemListSearchInput";
import { ItemListTable } from "../components/ItemListTable";
import ItemListColumnConfigModal from "../components/ItemListColumnConfigModal";
import SortOrderEditModal from "../components/SortOrderEditModal.tsx";
import SpecifiedCompareItemsModal from "../components/SpecifiedCompareItemsModal";
import ItemListValueEditModal from "../components/ItemListValueEditModal";
import ItemListPlacementEditModal from "../components/ItemListPlacementEditModal";
import type { ItemPlacementEditTarget } from "../components/ItemListPlacementEditModal";
import { getVisiblePlacementNeighbors } from "../model/item-list-placement";
import ItemEditor from "../components/ItemEditor";
import ItemGroupEditor from "../../item-groups/components/ItemGroupEditor";
import type { ItemListValueEditField } from "../components/ItemListValueEditModal";


export const ItemListPage = () => {

  const auth = useAuth();
  const authRef = React.useRef(auth);
  React.useEffect(() => {
    authRef.current = auth;
  }, [auth]);
  const navigate = useNavigate();
  const location = useLocation();
  const [searchParams, setSearchParams] = useSearchParams();
  const { items, fetchItems, fetchSingleItem, highlightedItem, focusItemRow, itemProcessToastEnabled, setItemProcessToastEnabled } = useItemContext();
  const [transferJob, setTransferJob] = React.useState<{ status: string } | null | undefined>(
    canAccessItemImport(auth) ? undefined : null,
  );
  const transferWasActive = React.useRef(false);
  const compareSiteParam = searchParams.get('compare_site');
  const searchParam = searchParams.get('q');
  const filterItemStatus = normalizeItemStatusFilter(searchParams.get('status'));
  const filterFloorReached = searchParams.get('floor_reached') === '1';
  const filterKeepDefaultPrice = normalizeKeepDefaultPriceFilter(searchParams.get('keep_default_price'));
  const filterCompareRange = normalizeCompareRangeFilter(searchParams.get('compare_range'));
  const filterMarketSite = normalizeMarketSiteFilter(searchParams.get('market_site'));
  const filterAccumulatedError = normalizeAccumulatedErrorFilter(searchParams.get('has_error'));

  const [itemGroups, setItemGroups] = React.useState<ItemGroupSummary[]>([]);
  const [shops, setShops] = React.useState<any[]>([]);

  const [errorMessage, setErrorMessage] = React.useState<string>("");
  const [itemListError, setItemListError] = React.useState<string>("");
  const setItemListErrorMessage = React.useCallback<ApiErrorSetter>((message, isConnectionFailure = false) => {
    if (isConnectionFailure) {
      setItemListError("");
      void authRef.current.refreshProcessReadiness();
      return;
    }
    setItemListError(message);
  }, []);
  React.useEffect(() => {
    if (!canAccessItemImport(authRef.current)) return;
    let live = true;
    const poll = async () => {
      try {
        const response = await api.get("/items/transfer/import/jobs/active/");
        if (!live) return;
        const active = response.data.job as { status: string } | null;
        setTransferJob(active);
        if (transferWasActive.current && !active) fetchItems(authRef.current, setItemListErrorMessage);
        transferWasActive.current = Boolean(active);
      } catch { if (live) setTransferJob(null); }
    };
    void poll();
    const timer = window.setInterval(poll, 3000);
    return () => { live = false; window.clearInterval(timer); };
  }, [fetchItems, setItemListErrorMessage]);

  const [selectedItems, setSelectedItems] = React.useState<Set<number>>(new Set());
  const [editingItem, setEditingItem] = React.useState<{ id: string; duplicated?: boolean } | null>(null);
  const [editingGroupId, setEditingGroupId] = React.useState<string | null>(null);
  const [filterCompareSite, setFilterCompareSite] = React.useState<string>("");     // 비교사이트 필터 ('', 'danawa', 'enuri', 'naver', 'direct', 'coupang')
  const [activeSearchKeyword, setActiveSearchKeyword] = React.useState<string>(""); // 실제 검색용
  const [disabledCompareSites, setDisabledCompareSites] = React.useState<Partial<Record<'danawa' | 'enuri' | 'naver', true>> | null>(null);

  const [itemListColumnPreferences, setItemListColumnPreferences] = React.useState<ItemListColumnPreferences>(
    createItemListColumnPreferences,
  );
  const [draftWidth, setDraftWidth] = React.useState<{ id: string; width: number } | null>(null);
  const resizeRef = React.useRef<{ id: string; pointerId: number; startX: number; startWidth: number } | null>(null);
  const savingWidthRef = React.useRef(false);
  const [savingWidth, setSavingWidth] = React.useState(false);
  const itemListColumnConfig = React.useMemo(
    () => resolveItemListColumnConfig(draftWidth
      ? { ...itemListColumnPreferences, widths: { ...itemListColumnPreferences.widths, [draftWidth.id]: draftWidth.width } }
      : itemListColumnPreferences),
    [itemListColumnPreferences, draftWidth],
  );
  const [columnConfigModalShow, setColumnConfigModalShow] = React.useState<boolean>(false);

  const saveColumnWidths = async (preferences: ItemListColumnPreferences) => {
    if (savingWidthRef.current) return;
    savingWidthRef.current = true;
    setSavingWidth(true);
    const previousPreferences = itemListColumnPreferences;
    setItemListColumnPreferences(preferences);
    await updateUserConfig({
      auth,
      payload: { item_list_column_config: preferences },
      setErrorMessage: () => {
        setItemListColumnPreferences(previousPreferences);
        toast.error("컬럼 너비를 저장하지 못했습니다.");
      },
    });
    savingWidthRef.current = false;
    setSavingWidth(false);
  };

  const handleResizeStart = (event: React.PointerEvent<HTMLSpanElement>, id: string) => {
    if (savingWidthRef.current) return;
    event.preventDefault();
    event.stopPropagation();
    const header = event.currentTarget.parentElement;
    if (!header) return;
    resizeRef.current = {
      id,
      pointerId: event.pointerId,
      startX: event.clientX,
      startWidth: header.getBoundingClientRect().width,
    };
    event.currentTarget.setPointerCapture(event.pointerId);
  };

  const handleResizeMove = (event: React.PointerEvent<HTMLSpanElement>) => {
    const resize = resizeRef.current;
    if (!resize || resize.pointerId !== event.pointerId) return;
    const width = Math.min(1000, Math.max(32, Math.round(resize.startWidth + event.clientX - resize.startX)));
    setDraftWidth({ id: resize.id, width });
  };

  const handleResizeEnd = (event: React.PointerEvent<HTMLSpanElement>) => {
    const resize = resizeRef.current;
    if (!resize || resize.pointerId !== event.pointerId) return;
    resizeRef.current = null;
    setDraftWidth(null);
    const width = Math.min(1000, Math.max(32, Math.round(resize.startWidth + event.clientX - resize.startX)));
    if (width === Math.round(resize.startWidth)) return;
    void saveColumnWidths({
      ...itemListColumnPreferences,
      widths: { ...itemListColumnPreferences.widths, [resize.id]: width },
    });
  };

  const [batchModalShow, setBatchModalShow] = React.useState<boolean>(false);
  const [batchActionType, setBatchActionType] = React.useState<ItemBatchActionType | ''>("");
  const [sortOrderModalShow, setSortOrderModalShow] = React.useState<boolean>(false);
  const [sortOrderModalTitle, setSortOrderModalTitle] = React.useState<string>("");
  const [sortOrderModalInitialValue, setSortOrderModalInitialValue] = React.useState<number | null>(null);
  const [sortOrderEditTarget, setSortOrderEditTarget] = React.useState<{ type: 'group'; id: string; name: string } | { type: 'item'; id: number; name: string } | null>(null);
  const [compareItemsSnapshotModalShow, setCompareItemsSnapshotModalShow] = React.useState<boolean>(false);
  const [compareItemsSnapshotModalItemId, setCompareItemsSnapshotModalItemId] = React.useState<number | null>(null);
  const [valueEditTarget, setValueEditTarget] = React.useState<{ itemId: string; field: ItemListValueEditField } | null>(null);
  const [placementEditTarget, setPlacementEditTarget] = React.useState<ItemPlacementEditTarget | null>(null);

  const [isInitialized, setIsInitialized] = React.useState<boolean>(false);

  // 초기 로드 시 URL 파라미터 초기화
  React.useEffect(() => {
    if (!isInitialized) {
      if (compareSiteParam) {
        setFilterCompareSite(compareSiteParam);
      }
      if (searchParam) {
        setActiveSearchKeyword(searchParam);
      }
      setIsInitialized(true);
    }
  }, [isInitialized, compareSiteParam, searchParam]);

  // 비교사이트/검색어 변경 시 데이터 로딩 (auth 객체 신원 변경으로 재조회하지 않음)
  React.useEffect(() => {
    if (!isInitialized) return;

    // console.log('🐸 Detecting Changed: ', searchParams)

    const compareSiteValue = compareSiteParam ? compareSiteParam : "";
    const searchValue = searchParam || "";

    // 필터 설정
    setFilterCompareSite(compareSiteValue);
    setActiveSearchKeyword(searchValue);

    // 데이터 로딩
    const params: { compare_site?: string; q?: string } = {};
    if (compareSiteValue) {
      params.compare_site = compareSiteValue;
    }
    if (searchValue.trim()) {
      params.q = searchValue.trim();
    }
    fetchItems(authRef.current, setItemListErrorMessage, Object.keys(params).length > 0 ? params : undefined);
  }, [compareSiteParam, searchParam, isInitialized, fetchItems, setItemListErrorMessage]);


  // 그룹 목록을 가져옴
  React.useEffect(() => {
    fetchItemGroupList({ auth, setErrorMessage: () => { }, setResult: setItemGroups });
  }, [auth]);

  // 상점 목록을 가져옴
  React.useEffect(() => {
    fetchShopList({ auth, tested: true, setErrorMessage: () => { }, setResult: setShops });
  }, [auth]);

  // 컬럼 설정 로드
  React.useEffect(() => {
    if (auth.apiReachable === false) {
      return;
    }

    retrieveUserConfig({
      auth: authRef.current,
      setErrorMessage: setItemListErrorMessage,
      setResult: (data: any) => {
        setItemListErrorMessage("", false);
        const savedPreferences = data?.item_list_column_config;
        setItemListColumnPreferences(normalizeItemListColumnPreferences(savedPreferences));
      },
    });
  }, [auth.apiReachable, setItemListErrorMessage]);

  React.useEffect(() => {
    if (auth.apiReachable === false) return;
    retrieveUserProfile({
      auth: authRef.current,
      setErrorMessage: setItemListErrorMessage,
      setResult: (profile) => setDisabledCompareSites(profile.compare_sites_disabled ?? {}),
    });
  }, [auth.apiReachable, setItemListErrorMessage]);

  React.useEffect(() => {
    const onChanged = (event: Event) => {
      setDisabledCompareSites((event as CustomEvent<Partial<Record<'danawa' | 'enuri' | 'naver', true>>>).detail);
    };
    window.addEventListener('compare-sites-disabled-changed', onChanged);
    return () => window.removeEventListener('compare-sites-disabled-changed', onChanged);
  }, []);

  const visibleItems = React.useMemo(
    () => filterItemListItems(items, filterItemStatus, filterFloorReached, filterKeepDefaultPrice, filterCompareRange, filterMarketSite, filterAccumulatedError),
    [items, filterItemStatus, filterFloorReached, filterKeepDefaultPrice, filterCompareRange, filterMarketSite, filterAccumulatedError],
  );

  React.useEffect(() => {
    setSelectedItems((current) => {
      const visibleItemIds = new Set(visibleItems.map((item) => item.id));
      const next = new Set(Array.from(current).filter((itemId) => visibleItemIds.has(itemId)));
      return next.size === current.size ? current : next;
    });
  }, [visibleItems]);

  // 작업이력 등에서 요청한 상품으로 스크롤
  React.useEffect(() => {
    const focusItemId = (location.state as { focusItemId?: string } | null)?.focusItemId;
    if (!focusItemId || visibleItems.length < 1) {
      return;
    }
    const exists = visibleItems.some((item) => String(item.id) === String(focusItemId));
    if (!exists) {
      navigate(".", { replace: true, state: {} });
      return;
    }
    const timer = window.setTimeout(() => {
      focusItemRow(focusItemId);
      navigate(".", { replace: true, state: {} });
    }, 50);
    return () => window.clearTimeout(timer);
  }, [visibleItems, location.state, navigate, focusItemRow]);


  // 아이템이 담긴 그룹 리스트
  const groupedItems = React.useMemo(() => {
    const groups: {
      [key: string]: {
        name: string;
        description: string | null;
        sort_order: number | null;
        items: any[];
      };
    } = {};
    visibleItems.forEach(item => {
      const groupId = item.itemgroup?.id ?? 'none';
      if (!groups[groupId]) {
        groups[groupId] = {
          name: item.itemgroup?.name ?? "(그룹 없음)",
          description: item.itemgroup?.description ?? null,
          sort_order: item.itemgroup?.sort_order ?? null,
          items: []
        };
      }
      groups[groupId].items.push(item);
    });
    return groups;
  }, [visibleItems]);

  // 아이템 선택 처리 함수
  const handleSelectItem = (itemId: number) => {
    const newSelectedItems = new Set(selectedItems);
    if (newSelectedItems.has(itemId)) {
      newSelectedItems.delete(itemId);
    } else {
      newSelectedItems.add(itemId);
    }
    setSelectedItems(newSelectedItems);
  };

  // 그룹 선택 처리 함수
  const handleSelectGroup = (itemIds: number[]) => {
    const newSelectedItems = new Set(selectedItems);
    const allInGroupSelected = itemIds.every(id => newSelectedItems.has(id));

    if (allInGroupSelected) {
      itemIds.forEach(id => newSelectedItems.delete(id));
    } else {
      itemIds.forEach(id => newSelectedItems.add(id));
    }
    setSelectedItems(newSelectedItems);
  };

  // 전체 선택 처리 함수
  const handleSelectAll = () => {
    if (isAllSelected) {
      setSelectedItems(new Set());
    } else {
      setSelectedItems(new Set(visibleItems.map(item => item.id)));
    }
  };

  const isAllSelected = visibleItems.length > 0 && visibleItems.every(item => selectedItems.has(item.id));

  const handleFilterChange = (compareSite: string) => {
    setFilterCompareSite(compareSite);
    // URL 파라미터도 업데이트
    const newSearchParams = new URLSearchParams(searchParams);
    if (compareSite) {
      newSearchParams.set('compare_site', compareSite);
    } else {
      newSearchParams.delete('compare_site');
    }
    setSearchParams(newSearchParams);
  };

  const handleStatusChange = (status: ItemStatusFilter) => {
    const newSearchParams = new URLSearchParams(searchParams);
    if (status) {
      newSearchParams.set('status', status);
    } else {
      newSearchParams.delete('status');
    }
    setSearchParams(newSearchParams);
  };

  const handleFloorReachedChange = (reached: boolean) => {
    const newSearchParams = new URLSearchParams(searchParams);
    if (reached) {
      newSearchParams.set('floor_reached', '1');
    } else {
      newSearchParams.delete('floor_reached');
    }
    setSearchParams(newSearchParams);
  };

  const handleKeepDefaultPriceChange = (value: KeepDefaultPriceFilter) => {
    const newSearchParams = new URLSearchParams(searchParams);
    if (value) newSearchParams.set('keep_default_price', value);
    else newSearchParams.delete('keep_default_price');
    setSearchParams(newSearchParams);
  };

  const handleCompareRangeChange = (value: CompareRangeFilter) => {
    const newSearchParams = new URLSearchParams(searchParams);
    if (value) newSearchParams.set('compare_range', value);
    else newSearchParams.delete('compare_range');
    setSearchParams(newSearchParams);
  };

  const handleMarketSiteChange = (value: MarketSiteFilter) => {
    const newSearchParams = new URLSearchParams(searchParams);
    if (value) newSearchParams.set('market_site', value);
    else newSearchParams.delete('market_site');
    setSearchParams(newSearchParams);
  };

  const handleAccumulatedErrorChange = (value: AccumulatedErrorFilter) => {
    const newSearchParams = new URLSearchParams(searchParams);
    if (value) newSearchParams.set('has_error', value);
    else newSearchParams.delete('has_error');
    setSearchParams(newSearchParams);
  };

  const handleSearchSubmit = (searchValue: string) => {
    setActiveSearchKeyword(searchValue); // 실제 검색 실행
    // URL 파라미터도 업데이트
    const newSearchParams = new URLSearchParams(searchParams);
    if (searchValue.trim()) {
      newSearchParams.set('q', searchValue.trim());
    } else {
      newSearchParams.delete('q');
    }
    setSearchParams(newSearchParams);
  };

  const getListParams = React.useCallback(() => {
    const params: { compare_site?: string; q?: string } = {};
    if (filterCompareSite) {
      params.compare_site = filterCompareSite;
    }
    if (activeSearchKeyword.trim()) {
      params.q = activeSearchKeyword.trim();
    }
    return Object.keys(params).length > 0 ? params : undefined;
  }, [filterCompareSite, activeSearchKeyword]);

  const refreshItems = React.useCallback(() => {
    const refresh = fetchItems(authRef.current, setItemListErrorMessage, getListParams());
    void authRef.current.refreshSubscriptionStatus();
    return refresh;
  }, [fetchItems, getListParams, setItemListErrorMessage]);

  React.useEffect(() => {
    if (isInitialized) {
      void authRef.current.refreshSubscriptionStatus();
    }
  }, [isInitialized]);

  const handleBatchActionChange = (action: ItemBatchActionType) => {
    setBatchActionType(action);
    setBatchModalShow(true);
  };

  const handleBatchModalClose = () => {
    setBatchModalShow(false);
    setBatchActionType('');
  };

  const handleBatchFocusItem = (itemId: string) => {
    handleBatchModalClose();
    window.setTimeout(() => {
      if (focusItemRow(itemId)) return;
      navigate({ pathname: '/items/', search: '' }, { state: { focusItemId: itemId } });
    }, 50);
  };

  const handleBatchComplete = () => {
    setSelectedItems(new Set());
    setBatchModalShow(false);
    setBatchActionType('');
    refreshItems();
  };

  const openGroupSortOrderModal = (groupId: string, groupData: any) => {
    if (groupId === 'none') {
      return;
    }
    setSortOrderEditTarget({ type: 'group', id: groupId, name: groupData.name });
    setSortOrderModalInitialValue(groupData.sort_order ?? 0);
    setSortOrderModalTitle(`그룹 우선순위 수정: ${groupData.name}`);
    setSortOrderModalShow(true);
  };

  const openItemSortOrderModal = (item: any) => {
    const itemName = item.name || `상품 ${item.id}`;
    setSortOrderEditTarget({ type: 'item', id: item.id, name: itemName });
    setSortOrderModalInitialValue(item.sort_order ?? 0);
    setSortOrderModalTitle(`상품 우선순위 수정: ${itemName}`);
    setSortOrderModalShow(true);
  };

  const handleSortOrderModalClose = () => {
    setSortOrderModalShow(false);
    setSortOrderEditTarget(null);
    setSortOrderModalTitle("");
    setSortOrderModalInitialValue(null);
  };

  const saveSortOrder = (
    target: { type: 'group'; id: string } | { type: 'item'; id: number },
    sortOrder: number,
    options?: { showToast?: boolean; closeModal?: boolean },
  ): Promise<void> => {
    const isGroup = target.type === 'group';
    const failMessage = isGroup ? '그룹 우선순위 수정에 실패했습니다.' : '상품 우선순위 수정에 실패했습니다.';

    return new Promise<void>((resolve, reject) => {
      const callbacks = {
        setErrorMessage: (message: string) => reject(new Error(message || failMessage)),
        setProcessMessage: () => { },
        setResult: () => {
          if (options?.showToast) {
            const label = isGroup ? '그룹' : '상품';
            toast.success(`${label} 우선순위가 #${sortOrder}로 변경되었습니다.`);
          }
          if (options?.closeModal) {
            handleSortOrderModalClose();
          }
          refreshItems();
          resolve();
        },
        setError: () => reject(new Error(failMessage)),
      };

      if (isGroup) {
        updateItemGroup({
          uid: target.id,
          payload: { sort_order: sortOrder },
          auth,
          ...callbacks,
        });
        return;
      }

      updateItem({
        auth,
        id: String(target.id),
        payload: { sort_order: sortOrder },
        ...callbacks,
      });
    });
  };

  const handleSortOrderAdjust = async (
    target: { type: 'group'; id: string } | { type: 'item'; id: number },
    sortOrder: number,
  ) => {
    try {
      await saveSortOrder(target, sortOrder);
    } catch (error: any) {
      const label = target.type === 'group' ? '그룹' : '상품';
      toast.error(error?.message || `${label} 우선순위 수정에 실패했습니다.`);
    }
  };

  const handleSortOrderSubmit = async (sortOrder: number) => {
    if (!sortOrderEditTarget) {
      throw new Error('수정 대상이 없습니다.');
    }

    await saveSortOrder(sortOrderEditTarget, sortOrder, { showToast: true, closeModal: true });
  };

  const handleToggleItemActive = async (item: any) => {
    setErrorMessage("");
    await updateItem({
      auth,
      id: String(item.id),
      payload: { is_active: !item.is_active },
      setErrorMessage,
      setProcessMessage: () => { },
      setResult: () => {
        toast.success(`상품 ${item.id}의 활성 상태가 변경되었습니다.`);
        refreshItems();
      },
      setError: () => { },
    });
  };

  const handleToggleItemKeepDefaultPrice = async (item: any) => {
    setErrorMessage("");
    await updateItem({
      auth,
      id: String(item.id),
      payload: { keep_default_price: !item.keep_default_price },
      setErrorMessage,
      setProcessMessage: () => { },
      setResult: () => {
        toast.success(`상품 ${item.id}의 복구 상태가 변경되었습니다.`);
        refreshItems();
      },
      setError: () => { },
    });
  };

  const openCompareItemsSnapshotModal = (item: any) => {
    setCompareItemsSnapshotModalItemId(item.id);
    setCompareItemsSnapshotModalShow(true);
  };

  const closeCompareItemsSnapshotModal = () => {
    setCompareItemsSnapshotModalShow(false);
    setCompareItemsSnapshotModalItemId(null);
  };

  const columnDataOpts = {
    filterCompareSite,
    filterItemStatus,
    filterFloorReached,
    filterKeepDefaultPrice,
    filterCompareRange,
    filterAccumulatedError,
    activeSearchKeyword,
    onOpenCompareItemsSnapshot: openCompareItemsSnapshotModal,
    onEditItemPrice: (item: any) => setValueEditTarget({ itemId: String(item.id), field: "price" }),
    onEditItemDiscount: (item: { id: string }) => setValueEditTarget({ itemId: String(item.id), field: "discount" }),
    onEditItemDefaultPrice: (item: { id: string }) => setValueEditTarget({ itemId: String(item.id), field: "price_default" }),
    onEditItemLowerLimit: (item: { id: string }) => setValueEditTarget({ itemId: String(item.id), field: "lower_limit" }),
    onEditItemTargetRank: (item: { id: string }) => setValueEditTarget({ itemId: String(item.id), field: "target_rank" }),
    onOpenItemEdit: (event: React.MouseEvent<HTMLAnchorElement>, item: any) => {
      if (event.button !== 0 || event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) return;
      event.preventDefault();
      setEditingItem({ id: String(item.id) });
    },
    onToggleItemActive: handleToggleItemActive,
    onToggleItemKeepDefaultPrice: handleToggleItemKeepDefaultPrice,
  };

  const itemCreateSearch = searchParams.toString();
  const itemCreatePath = itemCreateSearch ? `/items/new?${itemCreateSearch}` : "/items/new";
  const hasImportShop = hasSupportedImportShop(shops);

  if (transferJob === undefined) return <div>아이템 불러오기 상태를 확인하는 중입니다...</div>;
  if (transferJob) return <div className="alert alert-info" role="status">아이템을 불러오는 중입니다. 완료될 때까지 아이템을 조회하거나 변경할 수 없습니다. 현재 상태: {transferJob.status}</div>;

  return (
    <div className="item-list-page">
      {(errorMessage || itemListError) && (
        <div className="alert alert-danger alert-dismissible item-list-alert" role="alert">
          {errorMessage || itemListError}
          <button
            type="button"
            className="btn-close"
            onClick={() => {
              if (errorMessage) {
                setErrorMessage("");
              } else {
                setItemListError("");
              }
            }}
            aria-label="Close"
          ></button>
        </div>
      )}

      {/* 타이틀 */}
      <div className="page-head item-list-page-head d-flex justify-content-between align-items-center mb-0">
        <div className="d-flex align-items-center">
          <p className="fs-5 me-2">
            상품목록
          </p>

        </div>
        <div className="d-flex align-items-center">
          <div className="d-none d-sm-flex align-items-center justify-content-end">
            <ItemListSearchInput
              activeSearchKeyword={activeSearchKeyword}
              onSearchSubmit={handleSearchSubmit}
            />
          </div>
          <div className="d-flex align-items-center justify-content-end">
            {shops.length === 0 ? (
              <button
                className="btn btn-primary ms-2"
                disabled
                title="상품을 등록하려면 먼저 상점을 등록해야 합니다."
              >
                <span>상품등록</span>
              </button>
            ) : (
              <Link to={itemCreatePath} className="btn btn-primary ms-2">
                <span>상품등록</span>
              </Link>
            )}
            {canAccessItemImport(auth) && (!hasImportShop ? (
              <span className="ms-2" title="등록된 지원 마켓의 상점 API 정보가 없습니다. 상점 API 정보를 먼저 등록해 주세요." tabIndex={0}>
                <button className="btn btn-outline-primary" disabled style={{ pointerEvents: "none" }}>일괄등록</button>
              </span>
            ) : (
              <Link to="/items/import/" className="btn btn-outline-primary ms-2">일괄등록</Link>
            ))}
          </div>
        </div>
      </div>

      {/* 아이템 필터: 비교사이트 */}
      <div className="data-card item-list-data-card">
        <ItemListFilters
          filterCompareSite={filterCompareSite}
          onFilterChange={handleFilterChange}
          disabledCompareSites={disabledCompareSites}
          filterItemStatus={filterItemStatus}
          onStatusChange={handleStatusChange}
          filterFloorReached={filterFloorReached}
          onFloorReachedChange={handleFloorReachedChange}
          filterKeepDefaultPrice={filterKeepDefaultPrice}
          onKeepDefaultPriceChange={handleKeepDefaultPriceChange}
          filterCompareRange={filterCompareRange}
          onCompareRangeChange={handleCompareRangeChange}
          filterMarketSite={filterMarketSite}
          onMarketSiteChange={handleMarketSiteChange}
          filterAccumulatedError={filterAccumulatedError}
          onAccumulatedErrorChange={handleAccumulatedErrorChange}
          items={visibleItems}
        />

      {/* 아이템 배치액션 및 카운팅 */}
      <div className="row align-items-center">
        <div className="col">
          <ItemListBatchAction
            selectedItems={visibleItems.filter((item) => selectedItems.has(item.id))}
            onActionChange={handleBatchActionChange}
            subscriptionStatus={auth.subscriptionStatus}
            allowDryRun={["LOCAL", "DEV"].includes(import.meta.env.VITE_APP_ENV)}
            showInhouse={canShowInhouseFeatures(auth)}
          />
        </div>
        <div className="col-auto d-flex align-items-center gap-2">
          <button type="button" className="btn btn-sm btn-outline-secondary" onClick={() => setColumnConfigModalShow(true)} disabled={savingWidth}>컬럼설정</button>
          <button
            type="button"
            className={`btn btn-sm ${itemProcessToastEnabled ? 'btn-outline-primary' : 'btn-outline-secondary'}`}
            aria-pressed={itemProcessToastEnabled}
            onClick={() => setItemProcessToastEnabled(!itemProcessToastEnabled)}
          >
            알림 {itemProcessToastEnabled ? 'ON' : 'OFF'}
          </button>
        </div>
      </div>

      <ItemListTable
        visibleItems={visibleItems}
        groupedItems={groupedItems}
        selectedItems={selectedItems}
        highlightedItem={highlightedItem}
        itemListColumnConfig={itemListColumnConfig}
        itemListColumnPreferences={itemListColumnPreferences}
        draftWidth={draftWidth}
        columnDataOpts={columnDataOpts}
        filterItemStatus={filterItemStatus}
        filterFloorReached={filterFloorReached}
        filterKeepDefaultPrice={filterKeepDefaultPrice}
        filterCompareRange={filterCompareRange}
        filterAccumulatedError={filterAccumulatedError}
        isAllSelected={isAllSelected}
        onSelectAll={handleSelectAll}
        onSelectGroup={handleSelectGroup}
        onSelectItem={handleSelectItem}
        onOpenGroupSortOrder={openGroupSortOrderModal}
        onOpenGroupEdit={(event, groupId) => {
          if (event.button !== 0 || event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) return;
          event.preventDefault();
          setEditingGroupId(groupId);
        }}
        onOpenItemPlacement={setPlacementEditTarget}
        onOpenItemSortOrder={openItemSortOrderModal}
        onAdjustSortOrder={handleSortOrderAdjust}
        onResizeStart={handleResizeStart}
        onResizeMove={handleResizeMove}
        onResizeEnd={handleResizeEnd}
        onResizeCancel={() => { resizeRef.current = null; setDraftWidth(null); }}
      />
      </div>

      {/* 컬럼 설정 모달 */}
      <ItemListColumnConfigModal
        show={columnConfigModalShow}
        itemListColumnPreferences={itemListColumnPreferences}
        onHide={() => setColumnConfigModalShow(false)}
        onColumnConfigChange={(newPreferences) => { setItemListColumnPreferences(newPreferences); }}
      />

      {/* 우선순위 수정 모달 */}
      <SortOrderEditModal
        show={sortOrderModalShow}
        title={sortOrderModalTitle}
        currentSortOrder={sortOrderModalInitialValue}
        onHide={handleSortOrderModalClose}
        onSubmit={handleSortOrderSubmit}
      />

      {/* 배치 액션 모달 */}
      <ItemBatchActionModal
        show={batchModalShow}
        onHide={handleBatchModalClose}
        actionType={batchActionType}
        itemGroups={itemGroups}
        selectedItems={Array.from(selectedItems).map(String)}
        onComplete={handleBatchComplete}
        onFocusItem={handleBatchFocusItem}
        auth={auth}
      />

      <SpecifiedCompareItemsModal
        show={compareItemsSnapshotModalShow}
        itemId={compareItemsSnapshotModalItemId}
        onHide={closeCompareItemsSnapshotModal}
      />
      <ItemListValueEditModal
        auth={auth}
        itemId={valueEditTarget?.itemId ?? null}
        field={valueEditTarget?.field ?? "price"}
        onHide={() => setValueEditTarget(null)}
        onComplete={(itemId, field) => {
          if (field === "price" || field === "discount") refreshItems();
          else fetchSingleItem(itemId, authRef.current);
        }}
      />
      {placementEditTarget && (
        <ItemListPlacementEditModal
          auth={auth}
          item={placementEditTarget}
          itemGroups={itemGroups}
          previousItem={getVisiblePlacementNeighbors(visibleItems, placementEditTarget).previous}
          nextItem={getVisiblePlacementNeighbors(visibleItems, placementEditTarget).next}
          onHide={() => setPlacementEditTarget(null)}
          onComplete={refreshItems}
        />
      )}
      {editingItem && (
        <ItemEditor
          key={editingItem.id}
          itemId={editingItem.id}
          duplicated={editingItem.duplicated}
          onClose={() => setEditingItem(null)}
          onChanged={() => {
            setEditingItem(null);
            fetchSingleItem(editingItem.id, authRef.current);
            void authRef.current.refreshSubscriptionStatus();
          }}
          onDeleted={() => { setEditingItem(null); refreshItems(); }}
          onDuplicated={(id) => { refreshItems(); setEditingItem({ id, duplicated: true }); }}
          onOpenItem={(id) => setEditingItem({ id })}
        />
      )}
      {editingGroupId && (
        <ItemGroupEditor
          key={editingGroupId}
          groupId={editingGroupId}
          onClose={() => setEditingGroupId(null)}
          onChanged={() => {
            setEditingGroupId(null);
            refreshItems();
            fetchItemGroupList({ auth: authRef.current, setErrorMessage: () => {}, setResult: setItemGroups });
          }}
        />
      )}
    </div>
  )
}

export default ItemListPage;
